// @flow
import React, { useContext, useMemo, useCallback } from 'react';
import styled from 'styled-components';
import type { Node } from 'react';
import DynamicTable from '@atlaskit/dynamic-table';
import Button from '@atlaskit/button';
import { IconButton } from '@atlaskit/button/new';
import MoreIcon from '@atlaskit/icon/glyph/more';
import AvatarGroup from '@atlaskit/avatar-group';
import DropdownMenu, { DropdownItemGroup, DropdownItem } from '@atlaskit/dropdown-menu';
import { token } from '@atlaskit/tokens';
import OctoPermissionsContext from '../../providers/OctoPermissionsContext';
import { hasPermission } from '../../utils/commons';
import { OCTO_GLOBAL_PERMISSIONS } from '../../utils/consts';
import type { VirtualComponent, JiraProject, AKTableHead } from '../../types';
import { ASSIGNEE_TYPES_OBJECT } from '../assets/assigneeTypes.assets';
import IssuesCountLink from './IssuesCountLink';
import User from './User';
import AvatarAndName from './AvatarAndName';

const Empty = styled.div`
    h3,
    p {
        margin-bottom: 14px;
    }
    img {
        width: 160px;
        margin-top: 14px;
    }
`;

function DropdownOptions({ editAction, deleteAction, mergeAction }) {
    return (
        <DropdownItemGroup>
            <DropdownItem onClick={editAction}>Edit</DropdownItem>
            <DropdownItem onClick={mergeAction}>Merge</DropdownItem>
            <DropdownItem onClick={deleteAction}>Delete</DropdownItem>
        </DropdownItemGroup>
    );
}

type GetTableRowsInput = {
    virtualComponents: Array<?VirtualComponent>,
    editVirtualComponent: (virtualComponent: any) => void,
    setToDelete: Function,
    setToMerge: Function,
    allProjects: Array<?JiraProject>,
    baseInstanceUrl: string,
};

const MoreIconTrigger = (params) => {
    const { triggerRef, ...props } = params;
    return <IconButton {...props} icon={MoreIcon} label="more" ref={triggerRef} />;
};
function getProjectsContent({ projects }) {
    const avatarGroupData = (projects || []).map((p) => {
        return {
            key: p.jiraProjectId,
            name: p.jiraProjectName,
            src: p.jiraAvatarUrls ? p.jiraAvatarUrls.large : null,
            appearance: 'circle',
            size: 'medium',
            enableTooltip: true,
            onClick: () => window.PlatformAdapter.openProjectUrl(p.jiraProjectKey, p.style),
        };
    });
    if (!projects.length) return null;

    if (projects.length > 1) {
        return <AvatarGroup appearance="stack" size="medium" data={avatarGroupData} maxCount={4} />;
    }

    const [entry] = avatarGroupData;
    return (
        <AvatarAndName
            displayName={entry.name}
            avatarSrc={entry.src}
            avatarHref={entry.href}
            avatarOnClick={entry.onClick}
        />
    );
}

function getTableRows({ virtualComponents, editVirtualComponent, setToDelete, setToMerge }: GetTableRowsInput): Array {
    const rows = [];

    (virtualComponents || []).forEach((virtualComponent) => {
        if (virtualComponent) {
            const { id, name, description, projects, assigneeType, virtualComponentLead } = virtualComponent;

            if (!projects?.length) return;

            const projectsContent = getProjectsContent({ projects });

            const cells = [
                {
                    key: 'name',
                    name: 'Name',
                    content: (
                        <div
                            style={{ cursor: 'pointer', color: token('color.link') }}
                            onClick={() => editVirtualComponent(virtualComponent)}
                        >
                            {name}
                        </div>
                    ),
                },
                {
                    key: 'description',
                    name: 'Description',
                    content: (
                        <p
                            style={{
                                WebkitLineClamp: 1,
                                overflow: 'hidden',
                                display: '-webkit-box',
                                WebkitBoxOrient: 'vertical',
                            }}
                        >
                            {description}
                        </p>
                    ),
                },

                {
                    key: 'componentLead',
                    name: 'Component lead',
                    content: virtualComponentLead ? <User jiraUser={virtualComponentLead} /> : '',
                },
                {
                    key: 'assigneeType',
                    name: 'Default assignee',
                    content: (
                        <p
                            style={{
                                WebkitLineClamp: 1,
                                overflow: 'hidden',
                                display: '-webkit-box',
                                WebkitBoxOrient: 'vertical',
                            }}
                        >
                            {ASSIGNEE_TYPES_OBJECT[assigneeType]}
                        </p>
                    ),
                },
                {
                    key: 'issues',
                    name: 'Issues',
                    content: <IssuesCountLink virtualComponent={virtualComponent} />,
                },
                {
                    key: 'action',
                    name: 'Action',
                    content: (
                        <OctoPermissionsContext.Consumer>
                            {(permissions) => {
                                if (hasPermission(OCTO_GLOBAL_PERMISSIONS.MANAGE, permissions)) {
                                    return (
                                        <DropdownMenu trigger={MoreIconTrigger} position="bottom right">
                                            <DropdownOptions
                                                editAction={() => editVirtualComponent(virtualComponent)}
                                                deleteAction={() => setToDelete(virtualComponent)}
                                                mergeAction={() => setToMerge(virtualComponent)}
                                            />
                                        </DropdownMenu>
                                    );
                                }
                                return null;
                            }}
                        </OctoPermissionsContext.Consumer>
                    ),
                },
            ];
            cells.splice(2, 0, {
                key: 'projects',
                name: 'Projects',
                content: projectsContent,
            });

            rows.push({
                cells,
                key: `id-${id}`,
            });
        }
    });

    return rows;
}

function EmptyView({
    setIsFormOpen,
    hasVirtualComponents,
    filters,
}: {
    setIsFormOpen: (isFormOpen: boolean) => void,
    hasVirtualComponents: boolean,
    filters: any,
}): Node {
    const permissions = useContext(OctoPermissionsContext);
    const hasFiltersApplied = filters?.search || filters?.projects?.length > 0 || filters?.labels?.length > 0;
    const message =
        hasVirtualComponents || hasFiltersApplied
            ? "We couldn't find any components that matched your search"
            : 'There are no components in your projects yet.';

    const subMessage =
        !hasVirtualComponents &&
        'Components are subsections of a project. Use them to group issues within a project into smaller parts.';

    const img = hasVirtualComponents ? 'no-results' : 'no-components';

    return (
        <Empty>
            <img alt={img} src={`./${img}.jpeg`} />
            <h3>{message}</h3>
            {subMessage && <p>{subMessage}</p>}
            {!hasVirtualComponents && hasPermission(OCTO_GLOBAL_PERMISSIONS.MANAGE, permissions) && (
                <Button appearance="primary" onClick={() => setIsFormOpen(true)}>
                    Create One
                </Button>
            )}
        </Empty>
    );
}

type Props = {
    virtualComponents: Array<?VirtualComponent>,
    openCreateVirtualComponentModal: (isFormOpen: boolean) => void,
    editVirtualComponent: (virtualComponent: VirtualComponent) => void,
    setToDelete: Function,
    setToMerge: Function,
    sorting: { property: string, direction: 'asc' | 'desc' },
    onChangeSorting: (property: string, direction: string) => void,
    renderingWithinProjectContext?: boolean,
    filters: any,
};

const cells = [
    {
        key: 'name',
        content: 'Component',
        isSortable: true,
    },
    {
        key: 'description',
        content: 'Description',
    },
    {
        key: 'projects',
        content: 'Projects',
    },
    {
        key: 'virtual_component_lead_id',
        content: 'Component lead',
        isSortable: true,
    },
    {
        key: 'assigneeType',
        content: 'Default assignee',
        width: 12,
        isSortable: true,
    },
    {
        key: 'issues',
        content: 'Issues',
        width: 10,
    },
    {
        key: 'action',
        content: '',
        width: 5,
    },
];

export default function VirtualComponentsTable({
    virtualComponents,
    openCreateVirtualComponentModal,
    editVirtualComponent,
    setToDelete,
    setToMerge,
    sorting,
    onChangeSorting,
    filters,
}: Props): Node {
    const headers: AKTableHead = useMemo(
        () => ({
            cells,
        }),
        [],
    );

    const rows = useMemo(
        () =>
            getTableRows({
                virtualComponents,
                editVirtualComponent,
                setToDelete,
                setToMerge,
            }),
        [editVirtualComponent, virtualComponents, setToDelete, setToMerge],
    );

    const onSort = useCallback((data) => onChangeSorting(data.key, data.sortOrder.toLowerCase()), [onChangeSorting]);
    return (
        <DynamicTable
            emptyView={
                <EmptyView
                    setIsFormOpen={openCreateVirtualComponentModal}
                    hasVirtualComponents={virtualComponents && !!virtualComponents.length}
                    filters={filters}
                />
            }
            rows={rows}
            head={headers}
            isLoading={false}
            onSort={onSort}
            sortKey={sorting.property}
            sortOrder={sorting.direction.toUpperCase()}
            isFixedSize
        />
    );
}
